import { mutationOptions } from '@tanstack/react-query';

import apiClient from '@/constants/http';
import { extractApiError } from '@/hooks/mutations/utils';
import { getApiPlatform } from '@/utils/getPlatform';
import { Logger } from '@/utils/logging';

import type {
  StandbyJoinFailureReason,
  StandbyJoinMutationOptions,
  StandbyJoinPayload,
  StandbyJoinResponse,
} from './types';

export const STANDBY_JOIN_ERROR = 'STANDBY_JOIN_ERROR';

// The customer join endpoint shares createEntry's internals
// (squire-api standby-list.service.ts), so it reuses that service's existing
// exception codes for the shop-level and client-type gates, and the
// already-added code for a duplicate — confirmed against squire-api main.
// The customer-facing 409 additionally carries the existing entry's id +
// status url (the staff-side 400 does not), so the client can redirect
// instead of dead-ending — see spec Decisions.
const DUPLICATE_CODE = 'standby-list.entry.already-added';
const SHOP_DISABLED_CODE = 'standby-list.disabled';
const CLIENT_TYPE_DISABLED_CODE = 'standby-list.client-type.disabled';
// Confirmed against squire-api PR #19228 (feat/SQR-14762-standby-customer-join).
const THROTTLED_CODE = 'standby-list.join.throttled';

const readCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { code } = error as { code?: unknown };
  return typeof code === 'string' ? code : undefined;
};

const readRawDuplicateEntry = (
  error: unknown,
): { entryId: string; statusUrl: string } | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { entryId, statusUrl } = error as {
    entryId?: unknown;
    statusUrl?: unknown;
  };
  return typeof entryId === 'string' && typeof statusUrl === 'string'
    ? { entryId, statusUrl }
    : undefined;
};

/** Reads the duplicate entry's id + status url off a mutation error. */
export const getStandbyJoinDuplicateEntry = (
  error: unknown,
): { entryId: string; statusUrl: string } | undefined => {
  if (typeof error !== 'object' || error === null) return undefined;
  const { details } = error as { details?: unknown };
  return readRawDuplicateEntry(details);
};

/** Maps a mutation error to the `standby_entry_create_failed.reason` enum. */
export const getStandbyJoinFailureReason = (
  error: unknown,
): StandbyJoinFailureReason => {
  const code = readCode(error);
  if (code === DUPLICATE_CODE) return 'duplicate_active_entry';
  if (code === SHOP_DISABLED_CODE || code === CLIENT_TYPE_DISABLED_CODE) {
    return 'type_disabled';
  }
  if (code === THROTTLED_CODE) return 'rate_limited';
  return 'other';
};

const joinStandby = async ({
  shopId,
  requestedBarberId,
  serviceIds,
  scheduledFor,
}: StandbyJoinPayload): Promise<StandbyJoinResponse> => {
  try {
    const { data } = await apiClient.post<StandbyJoinResponse>(
      `/v1/standby-list/customer/${shopId}/join`,
      // Omit scheduledFor unless set so an unprovided day behaves exactly as
      // today (the server assigns today) — SQR-14768.
      {
        requestedBarberId,
        serviceIds,
        ...(scheduledFor && { scheduledFor }),
        platform: getApiPlatform(),
      },
    );

    return data;
  } catch (error) {
    const normalized = extractApiError(error, STANDBY_JOIN_ERROR);
    // Join errors log opaque ids only (spec Behaviour 7-8) — the raw error
    // can carry a server message or transport fields, so log the normalized
    // code, never the error object itself.
    Logger.error('Error joining standby list', { code: normalized.code });

    throw {
      ...normalized,
      details: readRawDuplicateEntry(error),
    };
  }
};

export const joinStandbyMutation = (options?: StandbyJoinMutationOptions) =>
  mutationOptions({
    ...options,
    mutationKey: ['standby', 'join'],
    mutationFn: joinStandby,
    // A retried join could double-create if the first call actually
    // succeeded server-side before the client saw a network error.
    retry: false,
  });
